import * as React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import {
    Button, Divider, IconButton, makeStyles, Menu, MenuItem, TextField, Typography,
} from '@material-ui/core';
import * as PatientStore from '../../app/store/PatientStore';
import VimoTaskContent from './VimoTaskContent';
import { appRoles } from '../../youwell-common/constants';
import apiUrls from '../../youwell-common/apiUrls';
import {
    AddIcon, EditIcon, ExpandIcon, PersonIcon, PrintIcon,
} from '../icons';
import urls from '../../app/urls';
import { getProgram } from '../utils/programUtils';
import { datestamp, fullDateTime } from '../../youwell-common/dateUtils';
import { hasValue } from '../../youwell-common/objectUtils';
import { DialogButton } from '../../youwell-common/components';

const useStyles = makeStyles(theme => ({
    root: {
    },
    topContent: {
        marginBottom: theme.spacing(6),
        display: 'flex',
    },
    existingElement: {
        flex: '1 1 auto',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    formDate: {
    },
    studentInput: {
        flex: '1 1 auto',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'flex-end',
    },
    inputLabel: {
        marginRight: theme.spacing(2),
    },
    input: {
        fontSize: theme.typography.h5.fontSize,
        width: 430,
    },
    print: {
        '@media print': {
            display: 'none',
        },
    },
    file: {
        display: 'flex',
        alignItems: 'center',
    },
    icon: {
        marginLeft: theme.spacing(1),
    },
    underline: {
        borderBottom: '1px solid',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
    },
    divider: {
        marginTop: theme.spacing(1),
    },
    menuIcon: {
        marginRight: theme.spacing(1),
    },
    cancelEdit: {
        marginRight: theme.spacing(1),
    },
    deleteButton: {
        marginTop: theme.spacing(1),
        color: theme.colors.red1,
    },
}));

const AddForm = ({
    studentId, patientProgramId, formId, onClose, patientProgramElement,
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));
    const vimoProgramId = useSelector(state => state.pageSettings.appConfig.vimoProgramId);
    const studentList = useSelector(state => state.patient.list);
    const hasLicense = useSelector(state => state.auth.profileData?.license);
    const profileData = useSelector(state => state.auth.profileData);
    const isTeacher = appRoles.isTherapist([profileData?.selectedRole?.role]);

    const task = useSelector(state => patientProgramElement?.task || (state.program.content && state.program.content[state.pageSettings.appConfig?.contentProgramId]?.modules
        ?.reduce((prev, curr) => prev || curr.tasks?.find(d => d.id === formId)
    , null)));

    const hasStudents = studentList?.length > 0;

    const taskContentRef = React.useRef();
    const [newStudentName, setNewStudentName] = React.useState('');
    const [selectedStudent, setSelectedStudent] = React.useState(null);
    const [validationError, setValidationError] = React.useState(false);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [addNewStudent, setAddNewStudent] = React.useState(false);
    const [responses, setResponses] = React.useState({});
    const [editing, setEditing] = React.useState(true);
    const [latestForm, setLatestForm] = React.useState(null);

    React.useEffect(() => {
        if (patientProgramElement?.responseForms?.length) {
            const lastResponse = patientProgramElement?.responseForms[patientProgramElement.responseForms.length - 1];
            setResponses(lastResponse.responses?.reduce((prev, curr) => (
                {
                    ...prev,
                    [curr.questionId]: { response: curr.response },
                }
            ), {}));
            setEditing(false);
            setLatestForm(lastResponse);
        }
    }, [patientProgramElement?.responseForms]);

    if (!task) {
        return null;
    }

    const readOnly = !isTeacher || !editing; // for now.. Maybe contactpersons should be allowed to add?

    const updateResponse = (updatedResponses) => {
        setResponses(prevResponses => updatedResponses.reduce((prev, { questionId, ...response }) => ({
            ...prev,
            [questionId]: { response },
        }), prevResponses));
    };

    const onSaveChanges = () => {
        const patientId = studentId || selectedStudent?.id;
        const responseData = {
            programId: vimoProgramId,
            taskId: task.id,
            taskVersionId: task.version,

            patientId,
            patientData: patientId ? null : {
                name: newStudentName,
            },
            patientProgramId: patientProgramId || getProgram(selectedStudent)?.id,
            therapistElementId: patientProgramElement?.id,
            responses: Object.keys(responses).map(questionId => ({
                questionId,
                response: responses[questionId]?.response,
            })),
        };

        const redirectToNew = (respData) => {
            if (patientId) {
                dispatch(PatientStore.actionCreators.redirect(`${urls.inside.student.home}/${patientId}`));
            } else if (respData.patientResult?.patient) {
                dispatch(PatientStore.actionCreators.redirect(`${urls.inside.student.home}/${respData.patientResult?.patient.id}`));
            }
        };

        const options = {
            callback: onClose || redirectToNew,
            reducerParams: { task, patientId },
        };

        if (patientProgramElement) {
            dispatch(PatientStore.actionCreators.insertTherapistTaskResponse(responseData, options));
        } else {
            dispatch(PatientStore.actionCreators.respondNewProgramElement(responseData, options));
        }
    };

    const save = () => {
        if (!studentId && !selectedStudent && !newStudentName) {
            setValidationError(true);
            return;
        }

        taskContentRef.current?.validateAndSave();
    };

    const cancelEditing = () => {
        setResponses(patientProgramElement?.responses);
        setEditing(false);
    };

    const handleNameChange = (event) => {
        if (isTeacher && !selectedStudent) {
            setAddNewStudent(true);
            setNewStudentName(event.target.value);
            setValidationError(!event.target.value);
        }
    };

    const openSelectStudent = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleInputClick = (event) => {
        if (selectedStudent) {
            openSelectStudent(event);
        }
    };

    const selectStudent = student => () => {
        if (student) {
            setSelectedStudent(student);
            setAddNewStudent(false);
            setNewStudentName('');
            setValidationError(false);
        } else {
            setSelectedStudent(null);
            setAddNewStudent(true);
        }
        setMenuAnchor(null);
    };

    const onDelete = () => {
        dispatch(PatientStore.actionCreators.deleteTherapistTask(patientProgramElement, { callback: onClose, reducerParams: { elementId: patientProgramElement.id, patientId: studentId || selectedStudent?.id } }));
    };

    return (
        <div className={classes.root}>
            <div className={classes.topContent}>
                {patientProgramElement && (
                    <div className={classes.existingElement}>
                        {latestForm && (
                            <Typography variant="h6" className={classes.formDate} title={`${latestForm.answeredByUserName} - ${fullDateTime(latestForm.submitted)}`}>
                                {translate('student.formRespondedDate', { created: datestamp(latestForm.submitted) })}
                            </Typography>
                        )}
                        {!editing && (
                            <IconButton onClick={() => setEditing(true)} color="secondary">
                                <EditIcon />
                            </IconButton>
                        )}
                    </div>
                )}


                {!readOnly && hasLicense && !studentId && (
                    <div className={classes.studentInput}>
                        <Typography variant="h5" color={validationError ? 'error' : 'secondary'} className={classes.inputLabel}>
                            {`${translate('addForm.formFor')}: `}
                        </Typography>
                        <TextField
                            value={addNewStudent ? newStudentName : selectedStudent?.name || ''}
                            onChange={handleNameChange}
                            onClick={handleInputClick}
                            error={validationError}
                            InputProps={{ className: classes.input }}
                            required
                            helperText={addNewStudent ? translate('addForm.creatingNewStudent') : translate('addForm.selectStudentOrCreateNew')}
                        />
                        <IconButton onClick={openSelectStudent}>
                            <ExpandIcon />
                        </IconButton>
                        <Menu
                            id="form_selectStudent-menu"
                            className={classes.dropMenu}
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                        >
                            {isTeacher && (
                                <MenuItem
                                    onClick={selectStudent(null)}
                                >
                                    <AddIcon color="secondary" className={classes.menuIcon} />
                                    <Typography color="inherit">
                                        {translate('addForm.createNewStudent')}
                                    </Typography>
                                </MenuItem>
                            )}
                            {hasStudents && <Divider />}
                            {studentList?.map(student => (
                                <MenuItem
                                    key={student.id}
                                    onClick={selectStudent(student)}
                                >
                                    <PersonIcon color="secondary" className={classes.menuIcon} />
                                    <Typography color="inherit">
                                        {student.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                        {validationError && <Typography variant="h5" color="error">*</Typography>}
                    </div>
                )}

                {false && task.printFileId && (
                    <div className={classes.print}>
                        <a href={`${apiUrls.baseUrl}/${apiUrls.media.file}/${task.printFileId}`} target="_blank" rel="noopener noreferrer">
                            <Typography className={classes.file}>
                                <span className={classes.underline}>
                                    {translate('page.clickToDownloadPrintfirendlyFile')}
                                </span>
                                <PrintIcon className={classes.icon} />
                            </Typography>
                        </a>
                    </div>
                )}
            </div>

            <VimoTaskContent
                task={task}
                ref={taskContentRef}
                onSaveChanges={onSaveChanges}
                onUpdateResponse={updateResponse}
                responses={responses}
                readOnly={readOnly}
            />
            {hasValue(patientProgramElement) && (
                <DialogButton
                    dialogTitle={translate('addForm.sureDelete')}
                    buttonText={translate('addForm.deleteForm')}
                    dialogCancelText={translate('common.cancel')}
                    dialogOkAction={onDelete}
                    buttonProps={{
                        className: classes.deleteButton,
                    }}
                />
            )}

            {validationError && (
                <Typography color="error" align="right">
                    {translate('addForm.pleaseGiveName')}
                </Typography>
            )}

            {!readOnly && (
                <div className={classes.actions}>
                    {editing && patientProgramElement && (
                        <Button onClick={cancelEditing} className={classes.cancelEdit}>
                            {translate('common.cancel')}
                        </Button>
                    )}
                    <Button variant="contained" color="secondary" onClick={save} disabled={!hasLicense}>
                        {translate('infobox.save')}
                    </Button>
                </div>
            )}
        </div>
    );
};

AddForm.propTypes = {
    studentId: PropTypes.string,
    patientProgramId: PropTypes.string,
    formId: PropTypes.string,
    onClose: PropTypes.func,
    patientProgramElement: PropTypes.object,
};

AddForm.defaultProps = {
    studentId: null,
    patientProgramId: null,
    onClose: null,
    patientProgramElement: null,
    formId: null,
};

export default AddForm;
